import { useEffect, useState } from "react";
import { CheckCircle2, LifeBuoy, RotateCcw, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate } from "../../lib/api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import CriticalPinDialog from "../CriticalPinDialog";

export default function SupportTab() {
    const [tickets, setTickets] = useState(null);
    const [filter, setFilter] = useState("active");
    const [active, setActive] = useState(null);
    const [reply, setReply] = useState("");
    const [pinTarget, setPinTarget] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () => api.get(`/support?status=${filter}`).then((r) => setTickets(r.data)).catch(() => setTickets([]));
    useEffect(() => { load(); }, [filter]); // eslint-disable-line

    const openTicket = async (t) => {
        const r = await api.get(`/support/${t.ticket_id}`);
        setActive(r.data);
    };

    const sendReply = async () => {
        if (!reply.trim()) return;
        setBusy(true);
        try {
            await api.post(`/support/${active.ticket_id}/messages`, { content: reply.trim() });
            setReply("");
            openTicket(active);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Gönderilemedi");
        } finally {
            setBusy(false);
        }
    };

    const close = async (t) => {
        await api.post(`/support/${t.ticket_id}/close`);
        toast.success(`${t.code} kapatıldı (pasife alındı)`);
        setActive(null);
        load();
    };

    const reopen = async (t) => {
        await api.post(`/support/${t.ticket_id}/reopen`);
        toast.success(`${t.code} yeniden açıldı`);
        setActive(null);
        load();
    };

    const remove = async (pin) => {
        setBusy(true);
        try {
            await api.delete(`/support/${pinTarget.ticket_id}`, { headers: { "X-Critical-Pin": pin } });
            toast.success("Destek talebi silindi");
            setPinTarget(null);
            setActive(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Silinemedi");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="admin-support-tab">
            <div className="mb-4 flex gap-2">
                {[["active", "Aktif"], ["passive", "Pasif"]].map(([k, l]) => (
                    <button key={k} onClick={() => setFilter(k)} className={`rounded-full border px-3.5 py-1.5 text-xs font-medium ${filter === k ? "border-primary bg-primary text-primary-foreground" : ""}`} data-testid={`support-filter-${k}`}>{l}</button>
                ))}
            </div>
            {!tickets ? (
                <div className="h-48 animate-pulse rounded-2xl bg-muted" />
            ) : (
                <div className="space-y-3">
                    {tickets.map((t) => (
                        <button key={t.ticket_id} onClick={() => openTicket(t)} className="w-full rounded-2xl border bg-card p-4 text-left shadow-sm transition-shadow hover:shadow-md" data-testid={`support-ticket-${t.ticket_id}`}>
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-3">
                                    <LifeBuoy className="h-5 w-5 text-terra-600" />
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono text-xs font-bold text-primary">{t.code}</span>
                                            <p className="font-semibold">{t.subject}</p>
                                        </div>
                                        <p className="mt-0.5 text-xs text-muted-foreground">{t.user_name} ({t.user_email}) • {t.messages.length} mesaj • {fmtDate(t.created_at)}</p>
                                    </div>
                                </div>
                                <Badge variant="outline" className={`text-[10px] ${t.status === "active" ? "border-emerald-200 bg-emerald-100 text-emerald-800" : "border-stone-200 bg-stone-100 text-stone-600"}`}>
                                    {t.status === "active" ? "Aktif" : "Pasif"}
                                </Badge>
                            </div>
                        </button>
                    ))}
                    {!tickets.length && <p className="rounded-2xl border border-dashed p-16 text-center text-sm text-muted-foreground" data-testid="support-empty">Bu durumda talep yok.</p>}
                </div>
            )}

            <Dialog open={!!active} onOpenChange={() => setActive(null)}>
                <DialogContent className="max-w-lg" data-testid="support-ticket-dialog">
                    {active && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-display"><span className="font-mono text-sm text-primary">{active.code}</span> — {active.subject}</DialogTitle>
                            </DialogHeader>
                            <div className="max-h-72 space-y-2 overflow-y-auto rounded-lg bg-muted/40 p-3">
                                {active.messages.map((m, i) => (
                                    <div key={i} className={`rounded-xl px-3 py-2 text-sm ${m.role === "admin" ? "bg-terra-600/10 ml-8" : "bg-card mr-8 border"}`}>
                                        <p className="text-[10px] font-bold uppercase text-muted-foreground">{m.sender} • {fmtDate(m.at)}</p>
                                        <p className="whitespace-pre-wrap">{m.content}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="flex gap-2">
                                <Textarea rows={2} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Yanıt yazın..." data-testid="support-reply-input" />
                                <Button onClick={sendReply} disabled={busy || !reply.trim()} className="bg-terra-600 text-white hover:bg-terra-700" data-testid="support-reply-send"><Send className="h-4 w-4" /></Button>
                            </div>
                            <div className="flex justify-between">
                                <Button size="sm" variant="outline" className="text-destructive" onClick={() => setPinTarget(active)} data-testid="support-delete"><Trash2 className="mr-1 h-3.5 w-3.5" /> Sil</Button>
                                {active.status === "active" ? (
                                    <Button size="sm" variant="outline" onClick={() => close(active)} data-testid="support-close"><CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Kapat</Button>
                                ) : (
                                    <Button size="sm" variant="outline" onClick={() => reopen(active)} data-testid="support-reopen"><RotateCcw className="mr-1 h-3.5 w-3.5" /> Yeniden Aç</Button>
                                )}
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            <CriticalPinDialog open={!!pinTarget} onClose={() => setPinTarget(null)} onSubmit={remove} actionLabel={pinTarget ? `${pinTarget.code} destek talebini kalıcı silme` : ""} busy={busy} />
        </div>
    );
}
